/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { CreateDivisionRequest } from '../models/create-division-request';
import { DivisionResponse } from '../models/division-response';
import { GenericResponse } from '../models/generic-response';
import { GetDivisionResponse } from '../models/get-division-response';
import { UpdateDivisionRequest } from '../models/update-division-request';

/**
 * Division APIs
 */
@Injectable({ providedIn: 'root' })
export class DivisionService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllDivisions()` */
  static readonly GetAllDivisionsPath = '/api/v1/divisions';

  /**
   * Get list of divisions.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllDivisions()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllDivisions$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to following fields:
       *   - division number
       *   - label
       */
      search?: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetDivisionResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      DivisionService.GetAllDivisionsPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetDivisionResponse>;
        })
      );
  }

  /**
   * Get list of divisions.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllDivisions$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllDivisions(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to following fields:
       *   - division number
       *   - label
       */
      search?: string;
    },
    context?: HttpContext
  ): Observable<GetDivisionResponse> {
    return this.getAllDivisions$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<GetDivisionResponse>): GetDivisionResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `createDivision()` */
  static readonly CreateDivisionPath = '/api/v1/divisions';

  /**
   * create Division.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createDivision()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createDivision$Response(
    params: {
      body: CreateDivisionRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<DivisionResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      DivisionService.CreateDivisionPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<DivisionResponse>;
        })
      );
  }

  /**
   * create Division.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createDivision$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createDivision(
    params: {
      body: CreateDivisionRequest;
    },
    context?: HttpContext
  ): Observable<DivisionResponse> {
    return this.createDivision$Response(params, context).pipe(
      map((r: StrictHttpResponse<DivisionResponse>): DivisionResponse => r.body)
    );
  }

  /** Path part for operation `getDivisionById()` */
  static readonly GetDivisionByIdPath = '/api/v1/Divisiones/{id}';

  /**
   * fetch Division details.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getDivisionById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDivisionById$Response(
    params: {
      /**
       * The Division id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<DivisionResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      DivisionService.GetDivisionByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<DivisionResponse>;
        })
      );
  }

  /**
   * fetch Division details.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getDivisionById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDivisionById(
    params: {
      /**
       * The Division id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<DivisionResponse> {
    return this.getDivisionById$Response(params, context).pipe(
      map((r: StrictHttpResponse<DivisionResponse>): DivisionResponse => r.body)
    );
  }

  /** Path part for operation `deleteDivisionById()` */
  static readonly DeleteDivisionByIdPath = '/api/v1/Divisiones/{id}';

  /**
   * Delete Division  by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteDivisionById()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteDivisionById$Response(
    params: {
      /**
       * The Division id that needs to be delete
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      DivisionService.DeleteDivisionByIdPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Delete Division  by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteDivisionById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteDivisionById(
    params: {
      /**
       * The Division id that needs to be delete
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.deleteDivisionById$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updateDivision()` */
  static readonly UpdateDivisionPath = '/api/v1/Divisiones/{id}';

  /**
   * update Division.
   *
   * update Division
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateDivision()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateDivision$Response(
    params: {
      /**
       * The Division id that needs to be updated
       */
      id: number;
      body: UpdateDivisionRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<DivisionResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      DivisionService.UpdateDivisionPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<DivisionResponse>;
        })
      );
  }

  /**
   * update Division.
   *
   * update Division
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateDivision$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateDivision(
    params: {
      /**
       * The Division id that needs to be updated
       */
      id: number;
      body: UpdateDivisionRequest;
    },
    context?: HttpContext
  ): Observable<DivisionResponse> {
    return this.updateDivision$Response(params, context).pipe(
      map((r: StrictHttpResponse<DivisionResponse>): DivisionResponse => r.body)
    );
  }
}
